import type { ReactNode } from 'react';

import { useCallback, useContext, useMemo } from 'react';

import useUniqueId from '@core/hooks/useUniqueId';

import { ConfirmationDialogContext } from './ConfirmationDialogContext';
import useConfirmationDialog from './useConfirmationDialog';

interface ConfirmationDialogProviderProps {
  children: ReactNode;

  /**
   * Optionally override the page's color mode.
   */
  colorMode?: 'dark' | 'light';
}

/**
 * Provides a confirm function bound to a single instance of a ConfirmationDialog component.
 * This is useful for sharing a single confirmation dialog across multiple components.
 */
export const ConfirmationDialogProvider = ({ children, colorMode }: ConfirmationDialogProviderProps) => {
  const uid = useUniqueId('ConfirmationDialogProvider');
  const defaultModalTargetId = uid('modal-target');
  const { confirm: originalConfirm, ConfirmationDialog } = useConfirmationDialog();

  // Wrap the confirm function to automatically inject defaultModalTargetId
  const confirm = useCallback(
    options => {
      return originalConfirm({
        ...options,
        modalTargetId: options?.modalTargetId || defaultModalTargetId,
      });
    },
    [originalConfirm, defaultModalTargetId],
  );

  return (
    <ConfirmationDialogContext.Provider
      value={useMemo(
        () => ({
          confirm,
        }),
        [confirm],
      )}
    >
      {children}

      <ConfirmationDialog />
      <div className="ModalWrapper" data-color-mode={colorMode} id={defaultModalTargetId} />
    </ConfirmationDialogContext.Provider>
  );
};

/**
 * Hook to access a confirm function bound to a single instance of the ConfirmationDialog component
 * rendered by the ConfirmationDialogProvider.
 */
export function useConfirmationDialogContext() {
  const value = useContext(ConfirmationDialogContext);
  if (!value) throw new Error('ConfirmationDialogContext.Provider is missing');
  return value;
}
